import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

// ========== TYPE ALIASES ==========
export type DbOfficer = Database["public"]["Tables"]["officers"]["Row"];
export type DbSite = Database["public"]["Tables"]["sites"]["Row"];
export type DbLateReport = Database["public"]["Tables"]["late_reports"]["Row"];
export type DbShiftPassword = Database["public"]["Tables"]["shift_passwords"]["Row"];
export type DbCheckIn = Database["public"]["Tables"]["check_ins"]["Row"];
export type DbIncident = Database["public"]["Tables"]["incidents"]["Row"];
export type DbCheckpoint = Database["public"]["Tables"]["checkpoints"]["Row"];
export type DbSchedule = Database["public"]["Tables"]["schedules"]["Row"];

// ========== OFFICERS ==========

export async function fetchOfficers() {
  const { data, error } = await supabase
    .from("officers")
    .select("*")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data;
}

export async function createOfficer(officer: Database["public"]["Tables"]["officers"]["Insert"]) {
  const { data, error } = await supabase
    .from("officers")
    .insert(officer)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateOfficer(id: string, updates: Database["public"]["Tables"]["officers"]["Update"]) {
  const { data, error } = await supabase
    .from("officers")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteOfficer(id: string) {
  const { error } = await supabase
    .from("officers")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

// ========== SITES ==========

export async function fetchSites() {
  const { data, error } = await supabase
    .from("sites")
    .select("*")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data;
}

export async function fetchSitesForOwner(ownerId: string) {
  const { data, error } = await supabase
    .from("sites")
    .select("*")
    .eq("owner_id", ownerId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data;
}

export async function createSite(site: Database["public"]["Tables"]["sites"]["Insert"]) {
  const { data, error } = await supabase
    .from("sites")
    .insert(site)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteSite(id: string) {
  const { error } = await supabase
    .from("sites")
    .delete()
    .eq("id", id);
  if (error) throw error;
}
// ========== LATE REPORTS ==========

export async function fetchLateReports() {
  const { data, error } = await supabase
    .from("late_reports")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

// ========== SHIFT PASSWORDS ==========

export async function fetchShiftPasswords() {
  const { data, error } = await supabase
    .from("shift_passwords")
    .select("*")
    .gte("expires_at", new Date().toISOString());
  if (error) throw error;
  return data;
}

function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let pwd = "";
  for (let i = 0; i < 8; i++) {
    pwd += chars[Math.floor(Math.random() * chars.length)];
    if (i === 3) pwd += "-";
  }
  return pwd;
}

export async function issueShiftPassword(officerId: string, expiryHours: number = 12, issuedBy: string = "Security Operator") {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + expiryHours * 60 * 60 * 1000);
  
  // Fetch previous password to ensure uniqueness
  const { data: prev } = await supabase
    .from("shift_passwords")
    .select("password")
    .eq("officer_id", officerId)
    .order("issued_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  // Generate a new password that's different from the previous one
  let newPassword = generatePassword();
  let attempts = 0;
  while (prev && newPassword === prev.password && attempts < 10) {
    newPassword = generatePassword();
    attempts++;
  }

  // Delete old passwords then insert new
  await supabase.from("shift_passwords").delete().eq("officer_id", officerId);
  
  const { data, error } = await supabase
    .from("shift_passwords")
    .insert({
      officer_id: officerId,
      password: newPassword,
      issued_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
      issued_by: issuedBy,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function issueAllPasswords(officerIds: string[], expiryHours: number = 12) {
  const results = await Promise.all(
    officerIds.map(id => issueShiftPassword(id, expiryHours))
  );
  return results;
}

// ========== CHECK-INS ==========

export async function fetchCheckIns() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("check_ins")
    .select("*")
    .gte("checked_at", today.toISOString())
    .order("checked_at", { ascending: false });
  if (error) throw error;
  return data;
}

// ========== INCIDENTS ==========

export async function fetchIncidents() {
  const { data, error } = await supabase
    .from("incidents")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function createIncident(incident: Database["public"]["Tables"]["incidents"]["Insert"]) {
  const { data, error } = await supabase
    .from("incidents")
    .insert(incident)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateIncident(id: string, updates: Database["public"]["Tables"]["incidents"]["Update"]) {
  const { data, error } = await supabase
    .from("incidents")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ========== CHECKPOINTS ==========

export async function fetchCheckpoints() {
  const { data, error } = await supabase
    .from("checkpoints")
    .select("*")
    .order("name", { ascending: true });
  if (error) throw error;
  return data;
}

export async function createCheckpoint(checkpoint: Database["public"]["Tables"]["checkpoints"]["Insert"]) {
  const { data, error } = await supabase
    .from("checkpoints")
    .insert(checkpoint)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCheckpoint(id: string) {
  const { error } = await supabase
    .from("checkpoints")
    .delete()
    .eq("id", id);
  if (error) throw error;
}
// ========== SCHEDULES ==========

export async function createSchedule(schedule: Database["public"]["Tables"]["schedules"]["Insert"]) {
  const { data, error } = await supabase
    .from("schedules")
    .insert(schedule)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteSchedule(id: string) {
  const { error } = await supabase
    .from("schedules")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

export async function fetchSchedules() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("schedules")
    .select("*")
    .gte("due_at", today.toISOString())
    .order("due_at", { ascending: true });
  if (error) throw error;
  return data;
}

export async function updateSite(id: string, updates: Database["public"]["Tables"]["sites"]["Update"]) {
  const { data, error } = await supabase
    .from("sites")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ========== BATCH SCHEDULE GENERATION ==========

export async function generateRotationSchedules(
  siteId: string,
  officerIds: string[],
  checkpointIds: string[],
  date: string, // YYYY-MM-DD
  shiftStart: string, // HH:MM
  shiftEnd: string, // HH:MM
  intervalMinutes: number = 60
) {
  if (officerIds.length === 0 || checkpointIds.length === 0) throw new Error("Need at least one officer and one checkpoint");

  const schedules: Database["public"]["Tables"]["schedules"]["Insert"][] = [];
  const [startH, startM] = shiftStart.split(":").map(Number);
  const [endH, endM] = shiftEnd.split(":").map(Number);

  const startTotal = startH * 60 + startM;
  const endTotal = endH * 60 + endM;
  const adjustedEnd = endTotal <= startTotal ? endTotal + 1440 : endTotal; // handle overnight

  let slotIndex = 0;
  for (let mins = startTotal; mins < adjustedEnd; mins += intervalMinutes) {
    const actualMins = mins % 1440;
    const h = Math.floor(actualMins / 60);
    const m = actualMins % 60;
    const dueAt = new Date(`${date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
    if (mins >= startTotal + 1440) {
      // next day
      dueAt.setDate(dueAt.getDate() + 1);
    }

    // Rotate: each officer gets a different checkpoint each slot
    for (let oi = 0; oi < officerIds.length; oi++) {
      const cpIndex = (oi + slotIndex) % checkpointIds.length;
      schedules.push({
        officer_id: officerIds[oi],
        checkpoint_id: checkpointIds[cpIndex],
        site_id: siteId,
        due_at: dueAt.toISOString(),
        status: "pending",
      });
    }
    slotIndex++;
  }

  // Delete existing schedules for this site+date range first
  const dayStart = new Date(`${date}T${shiftStart}:00`);
  const dayEnd = new Date(dayStart.getTime() + (adjustedEnd - startTotal) * 60000);
  await supabase.from("schedules").delete()
    .eq("site_id", siteId)
    .gte("due_at", dayStart.toISOString())
    .lte("due_at", dayEnd.toISOString());

  // Insert all in batches of 50
  for (let i = 0; i < schedules.length; i += 50) {
    const batch = schedules.slice(i, i + 50);
    const { error } = await supabase.from("schedules").insert(batch);
    if (error) throw error;
  }

  return schedules.length;
}

export async function updateSchedule(id: string, updates: Database["public"]["Tables"]["schedules"]["Update"]) {
  const { data, error } = await supabase
    .from("schedules")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ========== SITE INVITE CODES ==========

export async function claimSiteByCode(code: string) {
  const { data, error } = await supabase.rpc("claim_site_by_code", { _code: code.toUpperCase() });
  if (error) throw error;
  return data as string; // returns site_id
}

// ========== REAL-TIME SUBSCRIPTIONS ==========

export function subscribeToTable(
  table: "officers" | "sites" | "check_ins" | "late_reports" | "shift_passwords" | "incidents" | "checkpoints" | "schedules",
  callback: (payload?: any) => void
) {
  const channel = supabase
    .channel(`${table}_changes`)
    .on("postgres_changes", { event: "*", schema: "public", table }, (payload) => callback(payload))
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
